/**
 * Talk it through: the conversation after a reading (2026-10-20, Chris).
 *
 * The server decides everything that matters (backend
 * reader/service/talk_it_through.py): it counts the seeker's turns, refuses a
 * sixth, charges for five more, and says when a card is on offer. It sends
 * where the conversation stands as `talk` (the `talk-state` socket message,
 * the reading's load, the paid continue's answer), kept on `store.talk`:
 *
 *   { reading_id, started, used, allowed, remaining, turns_per_round,
 *     next_round, more_price, card_offer, card_question, closed, cards_pulled,
 *     can_pull }
 *
 * This module only reads it, so the app can draw the reply box, the quiet
 * countdown beside it and the paid continue. Pure.
 */

/**
 * The talk state for the reading on screen, or null: a state left from
 * another reading never draws this one's controls.
 */
export function talkFor(talk, readingId) {
  if (!talk) return null
  if (readingId && talk.reading_id && talk.reading_id !== readingId) return null
  return talk
}

/** A conversation exists for this reading. */
export function isTalking(talk) {
  return !!(talk && talk.started)
}

/** Turns left, or null when the server has not said. */
function remainingOf(talk) {
  if (!talk || typeof talk.remaining !== 'number') return null
  return talk.remaining
}

/**
 * May the reply box open for the conversation? Opened by a tap ("Talk it
 * through" / "Keep talking"), and only while turns remain: once they are
 * used, the offer to keep talking takes its place.
 */
export function talkComposerOpen({ open = false, talk = null, resumed = false } = {}) {
  if (!open) return false
  const left = remainingOf(talk)
  if (left !== null && left <= 0) return false
  // The Mystic closed it (the seeker said goodbye): the end chips take the
  // box's place until "Keep talking" is tapped again.
  if (talk && talk.closed && !resumed) return false
  return true
}

/**
 * The conversation has come to its end (4e, 2026-10-21): the turns are used,
 * or the Mystic closed it and the seeker has not picked it up again. The end
 * chips show then: Keep talking (5 more · price) · Share · Finish.
 */
export function talkEnded(talk, { resumed = false } = {}) {
  if (!talk || !talk.started) return false
  const left = remainingOf(talk)
  if (left !== null && left <= 0) return true
  return !!talk.closed && !resumed
}

/** At most this many cards pulled in one conversation (backend MAX_TALK_CARDS). */
export const MAX_TALK_CARDS = 2

/**
 * May the seeker pull a card herself from the "…" menu (4c)? Only while
 * turns remain beyond this one (a pull counts as a turn and is never the
 * last), fewer than two cards are out, and the talk is not closed. The
 * server has the last word and refuses a pull it does not allow.
 */
export function talkPullAllowed(talk) {
  if (!talk) return false
  // The server's word when it gives one (can_pull: the last answer wrote a
  // card_question, at least 2 turns remain, fewer than 2 cards pulled).
  if (typeof talk.can_pull === 'boolean') return talk.can_pull
  if (talk.closed) return false
  const left = remainingOf(talk)
  if (left === null || left <= 1) return false
  const pulled = Number(talk.cards_pulled)
  return !(Number.isFinite(pulled) && pulled >= MAX_TALK_CARDS)
}

/**
 * The "Pull a card" menu item: the same talk_pull_card action the Mystic's
 * offer chip sends (reader/service/state_management/state_classes/
 * reading_skill.py). Her own offer chip is reused when it is on screen.
 */
export function talkPullAction(actions, conversationType) {
  const list = Array.isArray(actions) ? actions : []
  const offered = list.find(a => a && a.action_type === 'talk_pull_card')
  return {
    key: 'talk_pull_card',
    action_type: 'talk_pull_card',
    conversation_type: (offered && offered.conversation_type) || conversationType || '',
    prompt_instructions: (offered && offered.prompt_instructions) || 'Pull a card for this',
    button_label: 'Pull a card',
  }
}

/**
 * The countdown beside the reply box: "4 left" ... "1 left", shown once
 * talking has started (after the first reply), gold on the last one.
 * Null when there is nothing to count.
 */
export function countdown(talk, { before = false } = {}) {
  const left = remainingOf(talk)
  if (!talk || left === null || left <= 0) return null
  // From a lesson (E2): "5 replies" before the first one.
  if (!talk.used) return before ? { text: `${left} ${left === 1 ? 'reply' : 'replies'}`, last: false } : null
  return { text: `${left} left`, last: left === 1 }
}

/** "Keep talking: 5 more · 50 ✦" (✦ is drawn as the carat mark). */
export function talkActionLabel(action, { pricesVisible = true } = {}) {
  if (!action) return ''
  const label = action.button_label || ''
  if (action.action_type !== 'talk_more') return label
  // Decision 11 (2026-10-26): no price before the Community opens.
  if (!pricesVisible) return label
  const price = Number(action.price_carats)
  return price > 0 ? `${label} · ${price} ✦` : label
}

/**
 * The chips to show. While the reply box is open for the conversation, the
 * chip that opens it is not needed beside it, and Share is not part of the
 * conversation (4d, 2026-10-21): it lives in the "…" menu then, and under
 * the summary and in the end chips otherwise. (The gift's deeper card keeps
 * its own chat and its chips as they are.)
 */
export function withoutOpenTalkChip(actions, { composerOpen = false } = {}) {
  const list = Array.isArray(actions) ? actions : []
  if (!composerOpen) return list
  const deeper = list.some(a => a && a.conversation_type === 'DRAW_DEEPER')
  return list.filter(a => !(a && ((a.action_type === 'chat_about_this' && a.conversation_type !== 'DRAW_DEEPER')
    || (!deeper && a.action_type === 'share_reading'))))
}

/**
 * The small line under a resumed card picker. A card pulled while talking it
 * through has none: the Mystic has just said why it is being pulled and the
 * question sits over the fan (2026-10-21, Chris: "the note at the bottom?").
 * Elsewhere: "Pick 1 card from above.", "Pick your last card from above.",
 * never "your remaining 1 card".
 */
export function pickerLine({ cardCount = 4, picked = 0, talk = false } = {}) {
  if (talk) return ''
  const remaining = Math.max(1, Number(cardCount) - Number(picked || 0))
  if (picked > 0 && remaining === 1) return 'Pick your last card from above.'
  if (picked > 0) return `Pick your remaining ${remaining} cards from above.`
  return `Pick ${remaining} card${remaining === 1 ? '' : 's'} from above.`
}

/**
 * The "…" menu's own end-of-reading items while the talk's menu is open: the
 * page's end-of-reading actions (Keep talking · Share · Finish) are already
 * there as Finish, Share and Pull a card, so they are not listed twice, and
 * "Keep talking" is not offered inside the conversation it continues
 * (2026-10-21: a reopened reading listed Finish Reading and Share twice).
 */
export function talkMenuExtras(actions, { talkMenuOpen = false, shown = [] } = {}) {
  const list = Array.isArray(actions) ? actions : []
  if (!talkMenuOpen) return list
  const seen = new Set(shown)
  return list.filter(a => !(a && (a.action_type === 'chat_about_this' || a.action_type === 'talk_more'
    || seen.has(a.action_type))))
}

/**
 * The cards pulled after a reading, for the top pane's table row and its
 * spotlight (2026-10-21 talk card design; 2026-10-22 API §6): the reading's
 * `talk_cards`, the gift room's drawn card (`gift_room.card`) and the panels
 * in the chat (`talk-card` frames), one per id, in the order drawn. A field
 * the chat has filled is never emptied by an older copy.
 * Labels: "Yours" for her card in a gift room; "Pulled", or "Pulled 1" /
 * "Pulled 2" once there are two.
 */
export function pulledCards ({ messages = [], talkCards = [], giftCard = null, giftLabel = '' } = {}) {
  const byId = new Map()
  const add = (c, kind) => {
    if (!c || !(c.card || c.name)) return
    const k = c.kind || kind || 'talk'
    const id = String(c.id || `${k}-${c.card || c.name}`)
    const was = byId.get(id) || {}
    const next = { ...was, id, kind: k }
    for (const key of ['card', 'image', 'question', 'line', 'reading']) {
      const v = c[key] !== undefined ? c[key] : (key === 'card' ? c.name : undefined)
      if (v) next[key] = v
      else if (next[key] === undefined) next[key] = ''
    }
    next.reversed = c.reversed !== undefined ? !!c.reversed : !!was.reversed
    byId.set(id, next)
  }
  if (giftCard) add(giftCard, 'gift')
  ;(talkCards || []).forEach((c) => add(c, 'talk'))
  ;(messages || []).forEach((m) => { if (m && m._context === 'talk-card' && m.talk_card) add(m.talk_card) })
  const list = [...byId.values()]
  const talks = list.filter((c) => c.kind !== 'gift')
  return list.map((c) => ({
    ...c,
    label: c.kind === 'gift' ? (giftLabel || 'Yours') : (talks.length > 1 ? `Pulled ${talks.indexOf(c) + 1}` : 'Pulled'),
  }))
}

/** The label inside a pulled card's panel: "Your card" in a gift room, else "Pulled". */
export function panelLabel (card) {
  // A gift room's card, and the card drawn from a lesson (lesson-redo E2.5), are "Your card".
  return card && (card.kind === 'gift' || card.lesson_draw) ? 'Your card' : 'Pulled'
}

/** The panel's question eyebrow: "The question you held" for a gift card (DESIGN-2 §37). */
export function panelQuestionLabel (card) {
  return card && card.kind === 'gift' ? 'The question you held' : 'Your question'
}
